/**
 * Adjustment form for creating stock adjustment requests
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Typography,
    Grid,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Alert,
    Chip,
    List,
    ListItem,
    ListItemText,
    Divider
} from "@mui/material";
import { DmsAltBox, DmsButton, intl } from "dmsbasereact";
import EditNoteIcon from "@mui/icons-material/EditNote";
import SendIcon from "@mui/icons-material/Send";
import { StockAdjustmentType } from "../../../constants/StockAdjustmentType";

/**
 * @param {Array} differences - Items with differences to adjust
 * @param {Array} adjustmentTypes - Available adjustment types
 * @param {Function} onSubmit - Callback when adjustment is submitted
 */
export const AdjustmentFormSection = ({ differences = [],
  adjustmentTypes = [],
  onSubmit = async () => false}) => {
    const [selectedItem, setSelectedItem] = useState(null);
    const [adjustmentType, setAdjustmentType] = useState("");
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleSelectItem = (item) => {
        setSelectedItem(item);
        setAdjustmentType("");
        setReason("");
    };

    const handleSubmit = async () => {
        if (!selectedItem || adjustmentType === "" || adjustmentType === null || adjustmentType === undefined || reason.length < 10) {
            return;
        }

        setSubmitting(true);
        const success = await onSubmit({
            batch_pk: selectedItem.batch_pk,
            physical_quantity: selectedItem.physical_quantity,
            adjustment_type: adjustmentType,
            reason: reason
        });

        if (success) {
            setSelectedItem(null);
            setAdjustmentType("");
            setReason("");
        }
        setSubmitting(false);
    };

    const getTypeLabel = (type) => {
        if (!type) return "";
        const typeValue = typeof type === 'object' ? type.value : type;
        return StockAdjustmentType.getText(typeValue);
    };

    return (
        <DmsAltBox.Card>
            <DmsAltBox.Header>
                <Box display="flex" alignItems="center" gap={1}>
                    <EditNoteIcon color="primary" />
                    <Typography variant="h6">
                        {intl.get("CreateAdjustment").d("Create Adjustment")}
                    </Typography>
                </Box>
            </DmsAltBox.Header>
            <DmsAltBox.Body>
                {differences.length === 0 ? (
                    <Alert severity="info">
                        {intl.get("NoDifferencesToAdjust").d(
                            "No differences found. Enter physical counts in the section above to identify discrepancies."
                        )}
                    </Alert>
                ) : (
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                {intl.get("SelectItemToAdjust").d("Select item to adjust:")}
                            </Typography>
                            <List dense sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
                                {differences.map((item, index) => (
                                    <React.Fragment key={item.batch_pk}>
                                        <ListItem
                                            button
                                            selected={selectedItem?.batch_pk === item.batch_pk}
                                            onClick={() => handleSelectItem(item)}
                                        >
                                            <ListItemText
                                                primary={item.batch_number}
                                                secondary={item.part_name}
                                            />
                                            <Chip
                                                label={
                                                    item.difference > 0
                                                        ? `+${item.difference}`
                                                        : item.difference
                                                }
                                                color={item.difference > 0 ? "success" : "error"}
                                                size="small"
                                            />
                                        </ListItem>
                                        {index < differences.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        </Grid>

                        <Grid item xs={12} md={8}>
                            {selectedItem ? (
                                <Box>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        <Typography variant="body2">
                                            <strong>{intl.get("Batch").d("Batch")}:</strong>{" "}
                                            {selectedItem.batch_number}
                                            {" | "}
                                            <strong>{intl.get("Product").d("Product")}:</strong>{" "}
                                            {selectedItem.part_name}
                                            {" | "}
                                            <strong>{intl.get("SystemQty").d("System")}:</strong>{" "}
                                            {selectedItem.system_quantity}
                                            {" | "}
                                            <strong>{intl.get("PhysicalQty").d("Physical")}:</strong>{" "}
                                            {selectedItem.physical_quantity}
                                            {" | "}
                                            <strong>{intl.get("Difference").d("Difference")}:</strong>{" "}
                                            {selectedItem.difference > 0 ? "+" : ""}
                                            {selectedItem.difference}
                                        </Typography>
                                    </Alert>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>
                                                    {intl.get("AdjustmentType").d("Adjustment Type")}
                                                </InputLabel>
                                                <Select
                                                    value={adjustmentType}
                                                    onChange={(e) => setAdjustmentType(e.target.value)}
                                                    label={intl.get("AdjustmentType").d("Adjustment Type")}
                                                >
                                                    {adjustmentTypes.map((type) => (
                                                        <MenuItem key={type.value} value={type.value}>
                                                            {getTypeLabel(type)}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                label={intl.get("Reason").d("Reason")}
                                                fullWidth
                                                multiline
                                                rows={3}
                                                value={reason}
                                                onChange={(e) => setReason(e.target.value)}
                                                placeholder={intl
                                                    .get("ReasonPlaceholder")
                                                    .d("Describe the reason for this adjustment (min 10 characters)...")}
                                                error={reason.length > 0 && reason.length < 10}
                                                helperText={
                                                    reason.length > 0 && reason.length < 10
                                                        ? intl.get("MinCharsRequired", { min: 10 }).d("Minimum 10 characters required")
                                                        : ""
                                                }
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Box display="flex" justifyContent="flex-end">
                                                <DmsButton
                                                    icon={<SendIcon />}
                                                    text={
                                                        submitting
                                                            ? intl.get("Submitting").d("Submitting...")
                                                            : intl.get("SubmitAdjustment").d("Submit Adjustment")
                                                    }
                                                    variant="primary"
                                                    onClick={handleSubmit}
                                                    disabled={
                                                        adjustmentType === "" ||
                                                        adjustmentType === null ||
                                                        adjustmentType === undefined ||
                                                        reason.length < 10 ||
                                                        submitting
                                                    }
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            ) : (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    minHeight={200}
                                >
                                    <Typography color="textSecondary">
                                        {intl.get("SelectItemFromList").d("Select an item from the list to create an adjustment")}
                                    </Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                )}
            </DmsAltBox.Body>
        </DmsAltBox.Card>
    );
};

AdjustmentFormSection.propTypes = {
    differences: PropTypes.arrayOf(
        PropTypes.shape({
            batch_pk: PropTypes.number.isRequired,
            batch_number: PropTypes.string,
            part_name: PropTypes.string,
            system_quantity: PropTypes.number,
            physical_quantity: PropTypes.number,
            difference: PropTypes.number
        })
    ),
    adjustmentTypes: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.number.isRequired,
            name: PropTypes.string
        })
    ),
    onSubmit: PropTypes.func
};

